import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import {
  ThemeProvider,
  mediaQuery,
  PALETTE
} from "@zendeskgarden/react-theming"
import styled from "styled-components"
import { Anchor } from "@zendeskgarden/react-buttons"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Span } from "@zendeskgarden/react-typography"
import { darkTheme } from "../styles/themes"
import Layout from "../components/layout"
import Sidebar from "../components/sidebar/sidebar"
import SEO from "../components/seo"
import HeroBanner from "../components/pages/heroBanner"
import FeatureBlock from "../components/pages/featureBlock"
import { H2, Subheading } from "../styles/typography"
import FeatureAccordion from "../components/pages/featureAccordion"
import ArrowLink from "../components/pages/arrowLink"
import Footer from "../components/footer"
import { gatsbyImageDataShape } from "../lib/prop-types"
import documentationIndexSidebar from "../navigation/index/documentation"

const Container = styled.div`
  display: flex;
  padding: ${props => `${props.theme.space.xxxl} ${props.theme.space.xl}`};
  color: ${props => props.theme.colors.foreground};
  background-color: ${props => props.theme.colors.background};

  ${props => mediaQuery("down", "md", props.theme)} {
    padding-right: ${props => props.theme.space.horz.md};
    padding-left: ${props => props.theme.space.horz.md};
  }

  ${props => mediaQuery("down", "xs", props.theme)} {
    padding: inherit 0;
  }
`

const MainContent = styled.div`
  width: ${props => props.theme.layout.index.maxWidth};
  margin: 0 auto;

  ${props => mediaQuery("down", "lg", props.theme)} {
    width: auto;
    max-width: ${props => props.theme.layout.index.maxWidth};
    margin: auto;
  }
`

const StyledHeroBanner = styled(HeroBanner)`
  margin-bottom: ${props => props.theme.space.xxxl};
`

const HeroFeatureGrid = styled.div`
  display: grid;
  grid-gap: ${props => props.theme.space.lg};
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: ${props => props.theme.space.base * 32}px;

  ${props => mediaQuery("down", "sm", props.theme)} {
    grid-template-columns: unset;
  }
`

const SDKs = styled.div`
  display: grid;
  grid-template: ${props =>
      `${props.theme.space.xl} 1fr ${props.theme.space.xl}`} / 1fr 10% 1fr;
  margin-bottom: ${props => props.theme.space.base * 28}px;

  ${props => mediaQuery("down", "xs", props.theme)} {
    display: block;
  }
`

const SDKsImagery = styled.div`
  grid-area: 1 / 1 / 3 / 3;
  margin-bottom: -35%;

  ${props => mediaQuery("down", "xs", props.theme)} {
    display: grid;
    grid-template: ${props =>
        `${props.theme.space.xl} 1fr ${props.theme.space.xl}`} / 1.5fr 15% 1fr;
    margin-bottom: 0;
  }
`

const SDKsBackground = styled(GatsbyImage)`
  ${props => mediaQuery("down", "xs", props.theme)} {
    grid-area: 1 / 1 / 3 / 3;
  }
`

const SDKsInfo = styled.div`
  z-index: 1;
  grid-area: 2 / 2 / 4 / 4;
  padding: ${props => props.theme.space.lg};
  background-color: ${props => props.theme.colors.background};

  ${props => mediaQuery("down", "xs", props.theme)} {
    padding-right: 0;
    padding-left: 0;
  }
`

const ChatImageContainer = styled.div`
  ${props => mediaQuery("up", "lg", props.theme)} {
    width: 45%;
    transform: translate(70%, -50%);
  }

  ${props => mediaQuery("between", ["sm", "lg"], props.theme)} {
    width: 50%;
    transform: translate(35%, -50%);
  }

  ${props => mediaQuery("down", "xs", props.theme)} {
    grid-area: 2 / 2 / 4 / 4;
    width: 100%;
  }
`

const StyledFeatureAccordion = styled(FeatureAccordion)`
  margin-bottom: ${props => props.theme.space.md};
`

const Guides = styled.div`
  margin-bottom: ${props => props.theme.space.base * 32}px;
`

const GuidesTitle = styled.div`
  max-width: ${props => props.theme.layout.document.maxWidth};
  margin: 0 auto;
  text-align: center;
`

const CustomData = styled.div`
  margin-bottom: ${props => props.theme.space.base * 32}px;
`

const Apps = styled.div`
  margin-bottom: ${props => props.theme.space.base * 32}px;
`

const AppsTitle = styled.div`
  max-width: ${props => props.theme.layout.document.maxWidth};
`

const AppsContent = styled.div`
  display: flex;
`

const AppsFeatureAccordion = styled(FeatureAccordion)`
  flex-basis: 55%;
  flex-shrink: 0;
`

const AppsAsideImage = styled.div`
  flex-basis: 500px;
  flex-grow: 0;
  margin-left: ${props => props.theme.space.lg};

  ${props => mediaQuery("down", "xs", props.theme)} {
    display: none;
  }
`

const ApiReference = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.space.xl};
  background-color: ${props => props.theme.colors.background};

  ${props => mediaQuery("down", "xs", props.theme)} {
    flex-direction: column;
    margin: ${props => `0 -${props.theme.space.horz.md}`};
    padding-right: ${props => props.theme.space.md};
    padding-left: ${props => props.theme.space.md};
  }
`

const ApiReferenceTitle = styled.div`
  flex-basis: 55%;
  flex-shrink: 0;
`

const ApiReferenceAsideImage = styled.div`
  flex-grow: 0;
  margin-left: ${props => props.theme.space.lg};

  ${props => mediaQuery("down", "xs", props.theme)} {
    order: -1;
    margin-bottom: ${props => props.theme.space.xl};
    margin-left: 0;
  }
`

export const imagesQuery = graphql`
  query {
    heroBanner: file(relativePath: { eq: "index/building-blocks.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE, width: 342)
      }
    }
    sdksBackground: file(
      relativePath: { eq: "index/conversational-experiences.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    sdksChatExample: file(
      relativePath: { eq: "index/chat-bouquet-purchase.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    guidesTickets: file(relativePath: { eq: "index/guides-tickets.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    guidesKb: file(relativePath: { eq: "index/guides-kb.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    guidesVoice: file(relativePath: { eq: "index/guides-voice.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    guidesSell: file(relativePath: { eq: "index/guides-sell.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    guidesLiveChat: file(relativePath: { eq: "index/guides-live-chat.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    customDataCustomObjects: file(
      relativePath: { eq: "index/custom-data-custom-objects.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    customDataProfiles: file(
      relativePath: { eq: "index/custom-data-profiles.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    customDataEvents: file(
      relativePath: { eq: "index/custom-data-events.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
    appsAside: file(relativePath: { eq: "index/shapes-in-beakers.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE, width: 500)
      }
    }
    apiReferenceAside: file(
      relativePath: { eq: "index/api-reference-code-block.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: NONE)
      }
    }
  }
`

const DocumentationIndex = ({ data: imageData }) => {
  const navigation = (
    <Sidebar
      data={documentationIndexSidebar}
      breadcrumbs={["Home"]}
      isTopLevel
      currentPage="/"
    />
  )

  return (
    <Layout navigation={navigation} collection="documentation">
      <SEO title="Documentation Home" />
      <Container>
        <MainContent>
          <StyledHeroBanner
            heading="Develop on the Zendesk Sunshine Platform"
            subheading="Zendesk Sunshine is our open and flexible platform, built natively on AWS. You can create unique customer experiences using our APIs and SDKs, connect data sources across your technology stack, and build any app or automation you want, using the languages you love."
            imageData={imageData.heroBanner}
            imageAltText="Blocks and a ruler representing a graph"
            backgroundColor="#f9f2e6"
            subheadingColor={PALETTE.kale[700]}
          />
          <HeroFeatureGrid>
            <FeatureBlock
              heading="Create a developer account"
              description="Try out the Sunshine Platform with a free, 14-day trial account."
              linkText="Create an account"
              linkHref="/documentation/developer-tools/getting-started/getting-a-trial-or-sponsored-account-for-development/"
            />
            <FeatureBlock
              heading="Create your first ticket"
              description="Get up and running on the Sunshine Platform using this 10-minute API quickstart guide."
              linkText="Get started"
              linkHref="/documentation/ticketing/getting-started/zendesk-api-quick-start/"
            />
            <FeatureBlock
              heading="Explore the Zendesk APIs"
              description="Use Postman, a popular API testing tool, to explore the Zendesk APIs."
              linkText="Explore the APIs"
              linkHref="/documentation/developer-tools/working-with-the-zendesk-apis/exploring-zendesk-apis-with-postman/"
            />
          </HeroFeatureGrid>
          <SDKs>
            <SDKsImagery>
              <SDKsBackground
                image={getImage(imageData.sdksBackground)}
                alt="A person looking at a phone screen"
              />
              <ChatImageContainer>
                <GatsbyImage
                  image={getImage(imageData.sdksChatExample)}
                  alt="A customer ordering flowers from a customer service agent within a messaging conversation"
                />
              </ChatImageContainer>
            </SDKsImagery>
            <SDKsInfo>
              <H2 bigger>Deliver rich conversational experiences</H2>
              <Subheading>
                {`Use our Zendesk SDKs to add messaging to your web, mobile, and
                social channels so customers can reach you instantly.`}
              </Subheading>
              <StyledFeatureAccordion
                level={3}
                sections={[
                  {
                    label: "Web",
                    description:
                      "Use the Web Widget API to configure the Web Widget settings and customize its appearance and functionality.",
                    linkText: "Web Widget API reference",
                    linkHref: "/api-reference/widget-messaging/web/core/"
                  },
                  {
                    label: "Android",
                    description:
                      "Add Messaging to your project to engage with your users from your Android app.",
                    linkText: "Get started",
                    linkHref:
                      "/documentation/zendesk-sdks/sdks/android/getting_started/"
                  },
                  {
                    label: "iOS",
                    description:
                      "Add Messaging to your project to engage with your users from your iOS app.",
                    linkText: "Get started",
                    linkHref:
                      "/documentation/zendesk-sdks/sdks/ios/getting_started/"
                  },
                  {
                    label: "Unity",
                    description:
                      "Add Messaging to your project to engage with your users from your Unity game.",
                    linkText: "Get started",
                    linkHref:
                      "/documentation/zendesk-web-widget-sdks/sdks/unity/getting_started/"
                  }
                ]}
              />
              <Span>
                <Span isBold>New to messaging? </Span>
                <Anchor
                  isExternal
                  href="https://support.zendesk.com/hc/en-us/articles/360057096813-About-Zendesk-messaging"
                  target="_blank"
                  externalIconLabel="Opens in a new window"
                >
                  Learn more about Messaging
                </Anchor>
              </Span>
            </SDKsInfo>
          </SDKs>
          <Guides>
            <GuidesTitle>
              <H2 bigger>Guides to solve your problems</H2>
              <Subheading>
                {`Access, integrate, and extend your data anywhere with an
                event-driven platform and robust APIs that let you build
                anything—inside or outside of Zendesk.`}
              </Subheading>
            </GuidesTitle>
            <FeatureAccordion
              level={3}
              showImages="right"
              imageBackgroundColor="#aed1cd"
              sections={[
                {
                  label: "Create and manage tickets",
                  labelSecondary: "Ticketing system",
                  description:
                    "Build custom ticket forms, create tickets programmatically and learn how to use our APIs to work with tickets.",
                  linkText: "Overview",
                  linkHref: "/documentation/ticketing",
                  imageData: getImage(imageData.guidesTickets),
                  imageAltText:
                    "A conversation thread within a support ticket between a customer and support agent"
                },
                {
                  label: "Manage your Knowledge Base",
                  labelSecondary: "Help Center",
                  description:
                    "Learn how to back up your knowledge base, or the ins and outs of our templating language, Curlybars.",
                  linkText: "Overview",
                  linkHref: "/documentation/help_center/",
                  imageData: getImage(imageData.guidesKb),
                  imageAltText:
                    "A help center article showing information about clothing sizes "
                },
                {
                  label: "Integrate your own call center solution",
                  labelSecondary: "Voice",
                  description:
                    "Integrate other telephony systems and learn how to update and create tickets based on calls and voicemails.",
                  linkText: "Overview",
                  linkHref: "/documentation/voice/",
                  imageData: getImage(imageData.guidesVoice),
                  imageAltText:
                    "A caller's profile image and phone number, with an option accept the incoming call"
                },
                {
                  label: "Learn how the Sell APIs work",
                  labelSecondary: "Sell",
                  description:
                    "Tap into Sell and build your own Sell-powered applications for the web, mobile and desktop.",
                  linkText: "Overview",
                  linkHref: "/documentation/sell/",
                  imageData: getImage(imageData.guidesSell),
                  imageAltText:
                    "An app extending the functionality of Zendesk's Sales CRM"
                },
                {
                  label: "Enable your app to act as a Chat agent",
                  labelSecondary: "Live Chat",
                  description:
                    "Use the Chat Conversations API to allow your application to act as a Zendesk Chat agent and interact with your website visitors.",
                  linkText: "Overview",
                  linkHref: "/documentation/live-chat/",
                  imageData: getImage(imageData.guidesLiveChat),
                  imageAltText:
                    "A customer chatting to a customer service agents requesting to change the size of the boquet they ordered"
                }
              ]}
            />
          </Guides>
          <CustomData>
            <FeatureAccordion
              level={3}
              inlineHeading="Connect all of your customer data"
              inlineSubheading="Sunshine helps you connect all your customer data for complete context—wherever it lives—and use our tools to build the best customer experiences."
              showImages="left"
              imageBackgroundColor="#f9f3e8"
              sections={[
                {
                  label: "Custom objects",
                  description:
                    "Store and connect new data sources, like products and order history, for greater context about your customers.",
                  linkText: "Getting started with custom objects",
                  linkHref:
                    "/documentation/custom-data/custom-objects/getting-started-with-custom-objects/",
                  imageData: getImage(imageData.customDataCustomObjects),
                  imageAltText:
                    "Data from external sources linked to an individual's profile in Zendesk"
                },
                {
                  label: "Profiles",
                  description:
                    "Build customer profiles with data from third-party applications to create a more complete picture of your customers.",
                  linkText: "Getting started with profiles",
                  linkHref:
                    "/documentation/ticketing/profiles/getting-started-with-profiles/",
                  imageData: getImage(imageData.customDataProfiles),
                  imageAltText:
                    "An person's profile extended with additional information from other connected platforms"
                },
                {
                  label: "Events",
                  description:
                    "Capture every customer interaction–like shopping cart, web, or mobile activity–to get a dynamic view of the customer journey.",
                  linkText: "Getting started with events",
                  linkHref:
                    "/documentation/ticketing/events/getting-started-with-events/",
                  imageData: getImage(imageData.customDataEvents),
                  imageAltText:
                    "A timeline of a customer activity used to help provide them with customer support"
                }
              ]}
            />
          </CustomData>
          <Apps>
            <AppsTitle>
              <H2 bigger>Build apps with any tech</H2>
              <Subheading>
                {`Zendesk apps are installed in the agent interface that extends
                the functionality of the Sunshine Platform. We've got the tools
                and frameworks to help you get started.`}
              </Subheading>
            </AppsTitle>
            <AppsContent>
              <AppsFeatureAccordion
                level={3}
                sections={[
                  {
                    label: "Learn about the Apps Framework",
                    description:
                      "Learn how the Zendesk Apps framework (ZAF) lets you build integrations and custom features to extend the functionality of Sunshine.",
                    linkText: "App developer guide",
                    linkHref:
                      "/documentation/apps/app-developer-guide/getting_started/"
                  },
                  {
                    label: "Try the framework yourself",
                    description:
                      "In this 15-minute quick start, you'll use the Zendesk Apps framework to access, add comments to, and create tickets.",
                    linkText: "Follow the quickstart guide",
                    linkHref:
                      "/documentation/apps/getting-started/test-drive-the-apps-framework/"
                  },
                  {
                    label: "Decide what type of app to make",
                    description:
                      "Understand the differences with client-side, server-side and integration apps.",
                    linkText: "Choose your own app adventure",
                    linkHref:
                      "/documentation/apps/getting-started/choose-your-own-app-adventure/"
                  }
                ]}
              />
              <AppsAsideImage>
                <GatsbyImage image={getImage(imageData.appsAside)} alt="" />
              </AppsAsideImage>
            </AppsContent>
          </Apps>
          <ThemeProvider theme={darkTheme}>
            <ApiReference>
              <ApiReferenceTitle>
                <H2 bigger>Read the API Reference</H2>
                <Subheading>
                  {`Zendesk's APIs are organized around REST principles. Our APIs
                  have resource-oriented URLs, accepts form-encoded request
                  bodies and return JSON-encoded responses.`}
                </Subheading>
                <ArrowLink href="/api-reference/">
                  Read the API Reference
                </ArrowLink>
              </ApiReferenceTitle>
              <ApiReferenceAsideImage>
                <GatsbyImage
                  image={getImage(imageData.apiReferenceAside)}
                  alt="A code block showing the response from a ticket creation endpoint"
                />
              </ApiReferenceAsideImage>
            </ApiReference>
          </ThemeProvider>
          <Footer />
        </MainContent>
      </Container>
    </Layout>
  )
}

DocumentationIndex.propTypes = {
  data: PropTypes.objectOf(gatsbyImageDataShape).isRequired
}

export default DocumentationIndex
